/* eslint complexity: ["error", {max: 22}] -- this overrides for the entire file */
/* eslint max-lines-per-function: ["error", {max: 160}] -- this overrides for the entire file */
import type { ComponentRef, JSX, RefObject } from "react";
import { useCallback } from "react";

import { useBottomSheetScrollableCreator } from "@gorhom/bottom-sheet";
import { FlashList } from "@shopify/flash-list";
import type { FlashListProps } from "@shopify/flash-list";
import isEmpty from "lodash/isEmpty";

import useHorizontalListScroll from "@/hooks/useHorizontalListScroll";
import Box from "@/ui/layout/Box";
import HorizontalListNavigationButtons, {
  HorizontalListScrollConfig,
} from "@/ui/layout/list/HorizontalListNavigationButtons";
import type { ListSeparatorType } from "@/ui/layout/list/ListComponents";
import {
  EmptyListComponent,
  getFlatListContentStyles,
  SpaceSeparatorComponent,
} from "@/ui/layout/list/ListComponents";
import { LayoutToken } from "@/ui/theme/tokens";
import { isAndroid, isIOS, isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

const FlatListContainer = ({
  children,
  isHorizontal,
}: {
  children: React.ReactNode;
  isHorizontal?: boolean;
}) => {
  // parent must be relative for absolute-positioned arrow buttons to display correctly
  if (isWeb() && isHorizontal) return <Box position="relative">{children}</Box>;
  // eslint-disable-next-line react/jsx-no-useless-fragment -- sonarjs/function-return-type requires consistent JSX return
  return <>{children}</>;
};

type FlashListRef<T = unknown> = ComponentRef<typeof FlashList<T>>;

interface FlatListProps<T> extends BaseViewProps {
  data?: T[];
  emptyListComponent?: () => JSX.Element;
  isBounceEnabled?: boolean;
  isDataLoading?: boolean;
  isFABVisible?: boolean;
  isHorizontal?: boolean;
  isInsideBottomSheet?: boolean;
  isRefreshControlLoading?: boolean;
  isScrollEnabled?: boolean;
  keyboardShouldPersistTaps?: FlashListProps<T>["keyboardShouldPersistTaps"];
  keyExtractor?: FlashListProps<T>["keyExtractor"];
  ListFooterComponent?: FlashListProps<T>["ListFooterComponent"];
  ListHeaderComponent?: FlashListProps<T>["ListHeaderComponent"];
  listRef?: RefObject<FlashListRef<T> | null>;
  onEndReached?: FlashListProps<T>["onEndReached"];
  onEndReachedThreshold?: FlashListProps<T>["onEndReachedThreshold"];
  onRefresh?: () => void;
  renderItem: FlashListProps<T>["renderItem"];
  separatorPadding?: number;
  separatorType?: ListSeparatorType;
  shouldDisableMaintainVisibleContentPosition?: boolean;
  shouldRemoveContentVerticalPadding?: boolean;
  shouldRemoveStyleHorizontalPadding?: boolean;
  shouldShowRefreshControl?: boolean;
}

// eslint-disable-next-line no-restricted-syntax -- this component has many props as it is a pass-through to the very complex Flatlist react-native component
const FlatList = <T,>({
  data,
  emptyListComponent,
  isBounceEnabled,
  isDataLoading,
  isFABVisible,
  isHorizontal,
  isInsideBottomSheet,
  isRefreshControlLoading = false,
  isScrollEnabled,
  keyboardShouldPersistTaps,
  keyExtractor,
  ListFooterComponent,
  ListHeaderComponent,
  listRef,
  onEndReached,
  onEndReachedThreshold,
  onRefresh,
  renderItem,
  separatorPadding,
  separatorType = "space",
  shouldDisableMaintainVisibleContentPosition,
  shouldRemoveContentVerticalPadding,
  shouldRemoveStyleHorizontalPadding,
  shouldShowRefreshControl,
  testID,
}: FlatListProps<T>) => {
  const baseTestID = joinTestIDs(testID, "flat-list");

  const {
    canScrollLeft,
    canScrollRight,
    effectiveListRef,
    onScroll,
    recheckScrollState,
  } = useHorizontalListScroll({
    isHorizontal,
    isListEmpty: isEmpty(data),
    listRef,
  });

  const handleLayout = useCallback(() => {
    if (!isWeb() || !isHorizontal) {
      return;
    }
    // Delay to ensure scroll position is set after layout completes
    // Without this, scrollToOffset fires before DOM is ready
    setTimeout(() => {
      effectiveListRef.current?.scrollToOffset({
        // eslint-disable-next-line @typescript-eslint/naming-convention -- FlashList API uses 'animated' not 'isAnimated'
        animated: false,
        offset: 0,
      });

      // Update navigation buttons state after layout
      recheckScrollState();
    }, HorizontalListScrollConfig.LayoutDelayMs);
  }, [effectiveListRef, isHorizontal, recheckScrollState]);

  const { paddingBottom, paddingLeft, paddingRight, paddingTop } =
    getFlatListContentStyles({
      isFABVisible,
      isHorizontal,
    });
  const ItemSeparatorComponent = useCallback(
    () => (
      <SpaceSeparatorComponent
        isHorizontal={isHorizontal}
        separatorPadding={separatorPadding}
        separatorType={separatorType}
      />
    ),
    [isHorizontal, separatorPadding, separatorType],
  );
  const ListEmptyComponent = useCallback(
    () => (
      <EmptyListComponent
        contentComponent={emptyListComponent}
        isEmpty={isEmpty(data)}
        isLoading={isDataLoading}
        testID={joinTestIDs(baseTestID, "empty-list")}
      />
    ),
    [baseTestID, emptyListComponent, data, isDataLoading],
  );

  const BottomSheetFlashList = useBottomSheetScrollableCreator();

  return (
    <FlatListContainer isHorizontal={isHorizontal}>
      {isHorizontal && (
        <HorizontalListNavigationButtons
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
          isListEmpty={isEmpty(data)}
          listRef={effectiveListRef}
          onRecheckScrollState={recheckScrollState}
          testID={joinTestIDs(baseTestID, "navigation")}
        />
      )}
      <FlashList
        bounces={isBounceEnabled}
        contentContainerStyle={{
          flex: isEmpty(data) ? 1 : undefined,
          paddingBottom: shouldRemoveContentVerticalPadding ? 0 : paddingBottom,
          paddingLeft: shouldRemoveStyleHorizontalPadding ? 0 : paddingLeft,
          paddingRight: shouldRemoveStyleHorizontalPadding ? 0 : paddingRight,
          paddingTop: shouldRemoveContentVerticalPadding ? 0 : paddingTop,
        }}
        data={data}
        horizontal={isHorizontal}
        ItemSeparatorComponent={ItemSeparatorComponent}
        key={isHorizontal ? "horizontal" : "vertical"}
        keyboardShouldPersistTaps={keyboardShouldPersistTaps}
        keyExtractor={keyExtractor}
        ListEmptyComponent={ListEmptyComponent}
        ListFooterComponent={ListFooterComponent}
        ListHeaderComponent={ListHeaderComponent}
        maintainVisibleContentPosition={{
          // eslint-disable-next-line @typescript-eslint/naming-convention
          disabled: shouldDisableMaintainVisibleContentPosition ?? false,
        }}
        onEndReached={onEndReached}
        onEndReachedThreshold={onEndReachedThreshold}
        onLayout={isHorizontal ? handleLayout : undefined}
        onRefresh={shouldShowRefreshControl ? onRefresh : undefined}
        onScroll={onScroll}
        ref={effectiveListRef}
        refreshing={
          shouldShowRefreshControl ? isRefreshControlLoading : undefined
        }
        // minor optimization to remove off-screen items to free memory
        // disabled on horizontal FlatLists due to a bug introduced from the switch to expo-image (on 1.113.0-dev)
        // this results in horizontal FlatLists having issues with their 3rd to 10th items (on the explore tab: missing thumbnails)
        removeClippedSubviews={!isHorizontal && (isAndroid() || isIOS())}
        renderItem={renderItem}
        renderScrollComponent={
          isInsideBottomSheet && !isWeb() ? BottomSheetFlashList : undefined
        }
        scrollEnabled={isScrollEnabled}
        showsHorizontalScrollIndicator={isWeb() && isHorizontal}
        showsVerticalScrollIndicator={false}
        style={{
          flex: 1,
          paddingHorizontal:
            isHorizontal || shouldRemoveStyleHorizontalPadding
              ? 0
              : LayoutToken.ContentPaddingHorizontal,
          width: "100%",
        }}
        testID={baseTestID}
      />
    </FlatListContainer>
  );
};

export default FlatList;
export type { FlashListRef, FlatListProps };
